import type { Editor } from '@tiptap/react';
import { type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { LayoutGrid } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Lib ---
import { isExtensionAvailable, isNodeInSchema } from '@/lib/tiptap-utils';
import { isSelectionTopNode } from '@/lib/zy-tiptap-utils';

// --- Icons ---
import { TableIcon } from '@/components/tiptap-editor/tiptap-icons/table-icon';

const REQUIRED_EXTENSIONS = ['table'];

export type TableInsertType = 'table' | 'grid' | 'zytable';

export interface TableDimensions {
    cols: number;
    rows: number;
}

export const DEFAULT_TABLE_DIMENSIONS: TableDimensions = { cols: 3, rows: 3 };

/**
 * Configuration for the table trigger functionality
 */
export interface UseTableTriggerButtonConfig {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor | null;
    /**
     * Whether the button should hide when table insertion is not available.
     * @default false
     */
    hideWhenUnavailable?: boolean;
    /**
     * Insert type: table or layout grid.
     * @default 'table'
     */
    mode?: TableInsertType;
    /**
     * Maximum number of rows in the grid selector.
     * @default 8
     */
    maxRows?: number;
    /**
     * Maximum number of columns in the grid selector.
     * @default 8
     */
    maxCols?: number;
    /**
     * When true, resets dimensions and focuses the columns input.
     */
    focusInputs?: boolean;
    /**
     * Callback function called after a successful table insertion.
     */
    onInserted?: (rows: number, cols: number) => void;
}

function clampDimension(value: number, max: number): number {
    if (!Number.isFinite(value)) {
        return 1;
    }
    return Math.min(Math.max(Math.trunc(value), 1), max);
}

function dimensionsToHoveredCell(dimensions: TableDimensions): { row: number; col: number } {
    return {
        row: dimensions.rows - 1,
        col: dimensions.cols - 1,
    };
}

/**
 * Checks if a table can be inserted in the current editor state
 */
export function canInsertTable(editor: Editor | null): boolean {
    if (!editor || !editor.isEditable) return false;
    return isExtensionAvailable(editor, REQUIRED_EXTENSIONS);
}

/**
 * Checks if a layout grid can be inserted in the current editor state
 */
export function canInsertGridLayout(editor: Editor | null): boolean {
    if (!editor || !editor.isEditable) return false;
    if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false;

    return editor.can().insertGridLayout({ rows: 1, cols: 1 });
}

/**
 * Inserts a table with the specified dimensions
 */
export function insertTable(editor: Editor | null, rows: number, cols: number): boolean {
    if (!editor || !canInsertTable(editor)) return false;

    try {
        return editor
            .chain()
            .focus()
            .insertTable({
                rows,
                cols,
                withHeaderRow: false,
            })
            .run();
    } catch (error) {
        console.error('Error inserting table:', error);
        return false;
    }
}

/**
 * Inserts a layout grid with the specified dimensions
 */
export function insertGridLayout(editor: Editor | null, rows: number, cols: number): boolean {
    if (!editor || !canInsertGridLayout(editor)) return false;

    try {
        return editor.chain().focus().insertGridLayout({ rows, cols }).run();
    } catch (error) {
        console.error('Error inserting layout grid:', error);
        return false;
    }
}

export function canInsertZyTableExample(editor: Editor | null): boolean {
    if (!editor || !editor.isEditable) return false;
    if (!isNodeInSchema('zyExample', editor)) return false;
    return isSelectionTopNode(editor);
}

/**
 * Inserts a zyExample table with the specified dimensions
 */
export function insertZyTableExample(editor: Editor | null, rows: number, cols: number): boolean {
    if (!editor || !canInsertZyTableExample(editor)) return false;

    try {
        return editor.chain().focus().insertZyExample('table', { caption: 'zyTable', rows, cols }).run();
    } catch (error) {
        console.error('Error inserting zyTable example:', error);
        return false;
    }
}

export function insertByType(editor: Editor | null, type: TableInsertType, rows: number, cols: number): boolean {
    if (type === 'grid') {
        return insertGridLayout(editor, rows, cols);
    }
    if (type === 'zytable') {
        return insertZyTableExample(editor, rows, cols);
    }
    return insertTable(editor, rows, cols);
}

export function canInsertByType(editor: Editor | null, type: TableInsertType): boolean {
    if (type === 'grid') {
        return canInsertGridLayout(editor);
    }
    if (type === 'zytable') {
        return canInsertZyTableExample(editor);
    }
    return canInsertTable(editor);
}

/**
 * Determines if the table trigger button should be shown
 */
export function shouldShowButton(editor: Editor | null, hideWhenUnavailable: boolean): boolean {
    if (!editor || !editor.isEditable) return false;

    const hasExtension = isExtensionAvailable(editor, REQUIRED_EXTENSIONS);
    if (!hasExtension) return false;

    return !hideWhenUnavailable || canInsertTable(editor);
}

function getLabelForType(type: TableInsertType): string {
    if (type === 'grid') {
        return 'Insert layout grid';
    }
    if (type === 'zytable') {
        return 'Insert zyTable';
    }
    return 'Insert table';
}

function getIconForType(type: TableInsertType) {
    return type === 'grid' ? LayoutGrid : TableIcon;
}

/**
 * Custom hook that provides table insertion functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * function MyTableButton() {
 *   const {
 *     isVisible,
 *     canInsert,
 *     isOpen,
 *     setIsOpen,
 *     hoveredCell,
 *     handleCellHover,
 *     handleCellClick,
 *     resetHoveredCell
 *   } = useTableTriggerButton({ editor })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <Popover open={isOpen} onOpenChange={setIsOpen}>
 *       <PopoverTrigger>Insert Table</PopoverTrigger>
 *       <PopoverContent>
 *         <TableGridSelector
 *           hoveredCell={hoveredCell}
 *           onCellHover={handleCellHover}
 *           onCellClick={handleCellClick}
 *           onMouseLeave={resetHoveredCell}
 *         />
 *       </PopoverContent>
 *     </Popover>
 *   )
 * }
 * ```
 */
export function useTableTriggerButton(config?: UseTableTriggerButtonConfig) {
    const {
        editor: providedEditor,
        hideWhenUnavailable = false,
        mode = 'table',
        maxRows = 8,
        maxCols = 8,
        focusInputs = false,
        onInserted,
    } = config || {};

    const { editor } = useTiptapEditor(providedEditor);
    const [isOpen, setIsOpen] = useState(false);
    const [dimensions, setDimensions] = useState<TableDimensions>(DEFAULT_TABLE_DIMENSIONS);
    const columnsInputRef = useRef<HTMLInputElement>(null);
    const rowsInputRef = useRef<HTMLInputElement>(null);

    const isVisible = shouldShowButton(editor, hideWhenUnavailable);
    const canInsert =
        useEditorState({
            editor,
            selector: (ctx: EditorStateSnapshot<Editor | null>) => canInsertByType(ctx.editor, mode),
        }) ?? false;

    useEffect(() => {
        if (!focusInputs) {
            return;
        }

        const frameId = requestAnimationFrame(() => {
            setDimensions(DEFAULT_TABLE_DIMENSIONS);

            const input = columnsInputRef.current;
            if (!input) {
                return;
            }
            input.focus();
            input.select();
        });

        return () => cancelAnimationFrame(frameId);
    }, [focusInputs]);

    const handleDimensionsChange = useCallback(
        (next: TableDimensions) => {
            setDimensions({
                cols: clampDimension(next.cols, maxCols),
                rows: clampDimension(next.rows, maxRows),
            });
        },
        [maxCols, maxRows],
    );

    const insertAtDimensions = useCallback(
        (next: TableDimensions) => {
            const clamped = {
                cols: clampDimension(next.cols, maxCols),
                rows: clampDimension(next.rows, maxRows),
            };
            const success = insertByType(editor, mode, clamped.rows, clamped.cols);
            if (success) {
                setIsOpen(false);
                setDimensions(DEFAULT_TABLE_DIMENSIONS);
                onInserted?.(clamped.rows, clamped.cols);
            }
            return success;
        },
        [editor, mode, maxCols, maxRows, onInserted],
    );

    const handleInsert = useCallback(() => {
        insertAtDimensions(dimensions);
    }, [dimensions, insertAtDimensions]);

    const handleCellHover = useCallback(
        (row: number, col: number) => {
            handleDimensionsChange({ cols: col + 1, rows: row + 1 });
        },
        [handleDimensionsChange],
    );

    const handleCellClick = useCallback(
        (row: number, col: number) => {
            insertAtDimensions({ cols: col + 1, rows: row + 1 });
        },
        [insertAtDimensions],
    );

    return {
        isVisible,
        canInsert,
        isOpen,
        setIsOpen,
        dimensions,
        hoveredCell: dimensionsToHoveredCell(dimensions),
        handleDimensionsChange,
        handleInsert,
        handleCellHover,
        handleCellClick,
        columnsInputRef,
        rowsInputRef,
        label: getLabelForType(mode),
        Icon: getIconForType(mode),
        maxRows,
        maxCols,
    };
}
